















































































































































































































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import MobileDetect from 'mobile-detect';

import { FormState } from '@/components/prine/enums/FormState';
import DetailedConfirmModal from '@/components/prine/DetailedConfirmModal.vue';
import SelectExternalUsers from '@/components/projects/external-users/SelectExternalUsers.vue';
import SelectMsUsers from '@/components/projects/ms-users/SelectMsUsers.vue';

import { companyWebservice } from '@/webservices/company';
import { phaseWebservice } from '@/webservices/phase';
import { pmPhaseWebservice } from '@/webservices/pmphase';
import { tradeWebservice } from '@/webservices/trade';
import { projectWebservice } from '@/webservices/project';
import { microsoftWebservice } from '@/webservices/microsoft';

import type { User } from '@/models/user';
import type { Company } from '@/models/company';
import type { Phase } from '@/models/phase';
import type { Trade } from '@/models/trade';
import type { PMPhase } from '@/models/pmphase';
import type { Project } from '@/models/project';
import PrineButton from '@/components/prine/PrineButton.vue';
import ConfirmProjectUpdateModal from '@/components/prine/ConfirmProjectUpdateModal.vue';
import type { Worksheet } from '@/models/worksheet';
import type { XProcess } from '@/models/xprocess';
import { xProcessWebservice } from '@/webservices/xprocess';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';

@Component({
    name: 'StoreUpdateProject',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasPermission },
    components: {
        DetailedConfirmModal,
        ConfirmProjectUpdateModal,
        SelectExternalUsers,
        SelectMsUsers,
        PrineButton,
    },
})
export default class StoreUpdateProject extends Vue {
    private get isDesktop() {
        const isNotDesktop = window.matchMedia('only screen and (max-width: 1300px)').matches;
        const md = new MobileDetect(window.navigator.userAgent);
        return !(md.mobile() || md.phone() || md.tablet() || isNotDesktop);
    }

    get userHasRightToDelete() {
        return (
            (this.userInfo.id === this.project.admin_id && hasPermission(PERMISSIONS.APPEX_PROJECT_DELETE_OWN)) ||
            hasPermission(PERMISSIONS.APPEX_PROJECT_DELETE_ALL)
        );
    }

    public FormState = FormState;

    // Data
    protected disabled: boolean = false;
    protected errorMsg: string = '';
    protected isConfirmModalActive: boolean = false;
    protected isConfirmUpdateModalActive: boolean = false;
    protected state: FormState = FormState.New;

    protected project: Project = {
        id: 0,
        admin_id: 0,
        title: '',
        description: '',
        trades: [],
        phases: [],
        pmphases: [],
        xprocesses: [],
        lessons: [],
        users: [],
        external_users: [],
        worksheets_count: 0,
        pm_worksheets_count: 0,
        applied_lessons_count: 0,
        pm_applied_lessons_count: 0,
        all_created_lessons_count: 0,
        ms_integration: null,
        ms_users: [],
        ms_team_id: null,
        company: {} as Company,
        company_id: 0,
    };

    protected projectCopy: Project = {} as Project;

    protected loading = {
        data: false,
        color: '#fdc05d',
        size: '40px',
    };
    @Getter('utils/currentLocale') private currentLocale: string;

    private phases = Array<Phase>();
    private pmphases = Array<PMPhase>();
    private xprocesses = Array<XProcess>();
    private trades = Array<Trade>();
    private returnPath: string = '/projects';
    private msIntegrationQueueStatus: string = '';
    private interval: ReturnType<typeof setInterval>;
    private affectedData: Map<string, number> = new Map();
    private msIntegrationRequired: boolean = false;
    private updateAffectedData: Map<string, number> = new Map();

    @Getter('account/company') private company: Company;
    @Getter('company/users') private users: User[];
    @Getter('account/userInfo') private userInfo: User;
    @Action('project/setFirstProjectOrNull') private setFirstProjectOrNull;

    // Mounted is called initially
    private mounted() {
        this.loadCompanyData();

        if (this.$route.query.hasOwnProperty('id')) {
            this.loadProject(Number(this.$route.query.id.toString()));
            this.state = FormState.Update;
        }
        if (this.$route.query.hasOwnProperty('returnPath')) {
            this.returnPath = this.$route.query.returnPath.toString();
        }
        if (this.company.can_use_ms_graph && this.state === FormState.New) {
            this.msIntegrationRequired = true;
        }
    }

    private destroyed() {
        clearInterval(this.interval);
    }

    @Watch('project.xprocesses')
    @Watch('project.trades')
    @Watch('project.phases')
    @Watch('project.xprocesses')
    private projectChanged() {
        if (this.state === FormState.Update) {
            const missingWorksheets: Worksheet[] = [];
            let missingWorksheetCount = 0;
            let missingAppliedLessonsCount = 0;
            let missingPmWorksheetCount = 0;
            let missingPmAppliedLessonsCount = 0;

            // compare pmphases
            if (this.projectCopy && this.projectCopy.pmphases) {
                const missingPmphases = this.projectCopy.pmphases.filter((pmphase) =>
                    this.project.pmphases.every((pmp) => pmphase.id !== pmp.id)
                );
                for (const pmphase of missingPmphases) {
                    if (pmphase.pm_worksheets) {
                        for (const pmworksheet of pmphase.pm_worksheets) {
                            missingPmWorksheetCount++;
                            missingPmAppliedLessonsCount += pmworksheet.pm_applied_lessons.length;
                        }
                    }
                }
            }

            // compare trades
            if (this.projectCopy && this.projectCopy.trades) {
                const missingTrades = this.projectCopy.trades.filter((trade) =>
                    this.project.trades.every((t) => trade.id !== t.id)
                );
                for (const trade of missingTrades) {
                    if (trade.worksheets) {
                        for (const worksheet of trade.worksheets) {
                            const index = missingWorksheets.findIndex((o) => o.id === worksheet.id);
                            if (index === -1) {
                                missingWorksheets.push(worksheet);
                            }
                        }
                    }
                }
            }

            // compare phases
            if (this.projectCopy && this.projectCopy.phases) {
                const missingPhases = this.projectCopy.phases.filter((phase) =>
                    this.project.phases.every((p) => phase.id !== p.id)
                );
                for (const phase of missingPhases) {
                    if (phase.worksheets) {
                        for (const worksheet of phase.worksheets) {
                            const index = missingWorksheets.findIndex((o) => o.id === worksheet.id);
                            if (index === -1) {
                                missingWorksheets.push(worksheet);
                            }
                        }
                    }
                }
            }

            for (const worksheet of missingWorksheets) {
                missingWorksheetCount++;
                missingAppliedLessonsCount += worksheet.applied_lessons.length;
            }

            this.updateAffectedData = new Map();

            // Trades Worksheets:
            this.updateAffectedData.set(
                this.$tc('keywords.trade', 2).toString() + ' ' + this.$tc('keywords.worksheets', 2).toString(),
                missingWorksheetCount
            );

            // Trades Applied lessons
            this.updateAffectedData.set(
                this.$tc('keywords.trade', 2).toString() + ' ' + this.$tc('keywords.appliedLessons', 2).toString(),
                missingAppliedLessonsCount
            );

            // Project Management Worksheets
            this.updateAffectedData.set(
                this.$t('keywords.projectManagement').toString() + ' ' + this.$tc('keywords.worksheets', 2).toString(),
                missingPmWorksheetCount
            );

            // Project Management Applied lessons
            this.updateAffectedData.set(
                this.$t('keywords.projectManagement').toString() +
                    ' ' +
                    this.$tc('keywords.appliedLessons', 2).toString(),
                missingPmAppliedLessonsCount
            );
        }
    }

    private saveOrModal() {
        // if there is removed data from the project, open a modal
        let shouldOpenModal = false;
        this.updateAffectedData.forEach((value) => {
            if (value > 0) {
                shouldOpenModal = true;
            }
        });

        if (shouldOpenModal) {
            this.toggleConfirmUpdateModal(false);
        } else {
            this.save();
        }
    }

    private toggleConfirmUpdateModal(answer: boolean) {
        // this.getAffectedData();
        this.isConfirmUpdateModalActive = !this.isConfirmUpdateModalActive;

        if (answer) {
            this.save();
        }
    }

    private reload() {
        this.loadProject(this.project.id);
    }

    private loadProject(projectId: number) {
        this.loading.data = true;
        projectWebservice.getById(projectId).then((response) => {
            this.project = response.data;
            this.project.users = this.project.users.filter((user) => user.id !== this.userInfo.id);
            this.loading.data = false;

            this.refreshStatus();
            this.projectCopy = { ...this.project };
            if (this.project.ms_integration === 'pending' || this.project.ms_integration === 'done') {
                this.msIntegrationRequired = true;
            }
        });
    }

    private loadCompanyData() {
        const self = this;

        // Load all users from the company the user is part of
        companyWebservice
            .getAllUsersByCompanyId(this.company.id)
            .then((response) => {
                const users = response.data.filter((user) => user.id !== self.userInfo.id && !user.disabled);
                self.$store.commit('company/setUsers', users);
            })
            .catch((error) => {
                console.error(error);
            });

        // Load all phases by the company
        phaseWebservice
            .load()
            .then((response) => {
                self.phases = response.data;
                // console.log(response.data);
            })
            .catch((error) => {
                console.error(error);
            });

        // Load all PM phases by the company
        pmPhaseWebservice
            .load()
            .then((response) => {
                self.pmphases = response.data;
                // console.log(response.data);
            })
            .catch((error) => {
                console.error(error);
            });

        // Load all X processes by the company
        xProcessWebservice
            .load()
            .then((response) => {
                self.xprocesses = response.data;
            })
            .catch((error) => {
                console.error(error);
            });

        // Load all trades by the company
        tradeWebservice
            .load(false)
            .then((response) => {
                self.trades = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private refreshStatus() {
        // Check MS integration queue status from time to time,
        // but only on update and in project is not already integrated with MS
        if (!this.interval && (this.project.ms_integration === 'pending' || this.project.ms_integration === 'init')) {
            console.log('check interval');
            this.interval = setInterval(() => {
                microsoftWebservice.getJobStatus(this.project.id).then((res) => {
                    if (res.data.status === 'Erledigt') {
                        this.msIntegrationQueueStatus = 'Microsoft Teams Integration abgeschlossen.';
                        this.project.ms_integration = 'done';
                        this.disabled = false;
                        clearInterval(this.interval);
                    } else {
                        this.msIntegrationQueueStatus = res.data.status;
                    }
                });
            }, 3000);
        }
    }

    private save() {
        this.disabled = true;
        if (!this.validate()) {
            this.disabled = false;
            return 0;
        }

        if (this.state === FormState.New) {
            if (this.msIntegrationRequired) {
                this.project.ms_integration = 'pending';
            } else {
                this.project.ms_users = [];
            }

            projectWebservice
                .store(this.project)
                .then((response) => {
                    this.project = response.data;
                    this.setProject(this.project).then(() => {
                        this.reload();
                        this.state = FormState.Update;
                        this.$router.push({ name: 'UpdateProject', query: { id: this.project.id.toString() } });
                        this.disabled = false;
                    });
                })
                .catch((error) => {
                    console.error(error);
                    this.disabled = false;
                });
        } else if (this.state === FormState.Update) {
            if (
                this.msIntegrationRequired &&
                this.project.ms_integration !== 'done' &&
                this.project.ms_integration !== 'pending'
            ) {
                this.project.ms_integration = 'init';
            } else if (!this.msIntegrationRequired) {
                this.project.ms_users = [];
            }
            projectWebservice
                .update(this.project)
                .then(() => {
                    this.setProject(this.project).then(() => {
                        if (!this.msIntegrationRequired) {
                            this.$router.push({ path: '/projects' });
                        } else {
                            this.reload();
                            this.disabled = false;
                        }
                    });
                })
                .catch((error) => {
                    console.error(error);
                    this.disabled = false;
                });
        }

        this.refreshStatus();
    }

    private setProject(project: Project) {
        return projectWebservice
            .loadOneByUser(project.id)
            .then((response) => {
                this.$store.commit('project/setSelectedProject', response.data);
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private toggleConfirmModal(answer: boolean = false) {
        this.getAffectedData();
        this.isConfirmModalActive = !this.isConfirmModalActive;

        if (answer) {
            this.remove();
        }
    }

    private remove() {
        this.disabled = true;
        projectWebservice
            .remove(this.project)
            .then((response) => {
                this.$buefy.toast.open({
                    duration: 3000,
                    message: 'Successfully removed the project',
                    type: 'is-success',
                });
                this.setFirstProjectOrNull().then(() => {
                    this.$router.push({ path: '/projects' });
                });
            })
            .catch((error) => {
                console.log(error);
                this.disabled = false;
            });
    }

    private validate() {
        if (!this.project.title || this.project.title === '') {
            this.errorMsg = 'title';
            return 0;
        } else {
            return true;
        }
    }

    private fullname({ firstname, lastname }) {
        return `${firstname} ${lastname}`;
    }

    private bkpTrade({ bkp, title }) {
        return `${bkp} ${title}`;
    }

    private isDuplicateName(name: string) {
        if (name !== '') {
            projectWebservice.isDuplicateName(name).then((response) => {
                if (response.data.found) {
                    this.errorMsg = 'duplicateTitle';
                } else {
                    this.errorMsg = '';
                }
            });
        }
    }

    private getAffectedData() {
        this.affectedData = new Map();

        this.affectedData.set(
            this.$tc('keywords.trade', 2).toString() + ' ' + this.$tc('keywords.worksheets', 2).toString(),
            this.project.worksheets_count
        );

        this.affectedData.set(
            this.$tc('keywords.trade', 2).toString() + ' ' + this.$tc('keywords.appliedLessons', 2).toString(),
            this.project.applied_lessons_count
        );

        this.affectedData.set(
            this.$t('keywords.projectManagement').toString() + ' ' + this.$tc('keywords.worksheets', 2).toString(),
            this.project.pm_worksheets_count
        );

        this.affectedData.set(
            this.$t('keywords.projectManagement').toString() + ' ' + this.$tc('keywords.appliedLessons', 2).toString(),
            this.project.pm_applied_lessons_count
        );
    }
}
